/* eslint-disable max-lines */
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ExceptionTrackingService } from '@com-delta-idp/mnx-utils';
import {
  EXCEPTION_CATEGORY_ENUM,
  IExceptionMetadata,
  SwapAuthForAccessTokens,
  SwapAuthForAccessTokensParams
} from '../../models';
import { UtilService } from '../util-service/util-service';
const fileName = 'SwapAuthForAccessTokenService';
@Injectable({
  providedIn: 'root'
})
export class SwapAccessTokenService {
  accessTokenHeader = new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded'
  });

  constructor(
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) private platformId: Object,
    private http: HttpClient,
    private utils: UtilService,
    private exceptionTracking: ExceptionTrackingService
  ) {}

  async swapAuthForAccessTokens(
    swapAuthForAccessTokensParams: SwapAuthForAccessTokensParams,
    mfaClientIdForSwap?: boolean
  ): Promise<SwapAuthForAccessTokens> {
    if (
      this.swapAuthForAccessTokensParamsValCheck(swapAuthForAccessTokensParams)
    ) {
      return this.errorResponse('ERROR_MSG_223');
    }
    try {
      return await this.responseOnPingSwapAuthForAccessUrl(
        swapAuthForAccessTokensParams,
        mfaClientIdForSwap
      );
    } catch (exception: unknown) {
      return this.exceptionHandling(exception);
    }
  }

  async responseOnPingSwapAuthForAccessUrl(
    swapAuthForAccessTokensParams: SwapAuthForAccessTokensParams,
    mfaClientIdForSwap?: boolean
  ): Promise<SwapAuthForAccessTokens> {
    const body = this.bodyValueWithParams(
      swapAuthForAccessTokensParams,
      mfaClientIdForSwap
    );
    const pingSwapAuthForAccessUrl = this.utils.getPingServiceUrl(
      swapAuthForAccessTokensParams.oauthConfig,
      undefined,
      undefined
    ).swapAuthForAccessTokensUrl;
    if (isPlatformBrowser(this.platformId) && pingSwapAuthForAccessUrl) {
      return await this.returnResponseData(
        pingSwapAuthForAccessUrl,
        body,
        swapAuthForAccessTokensParams
      );
    } else {
      return this.errorResponse('ERROR_MSG_224');
    }
  }

  errorResponse(errCode: string) {
    return {
      errors: [
        {
          code: errCode
        }
      ]
    };
  }

  bodyValueWithParams(
    swapAuthForAccessTokensParams: SwapAuthForAccessTokensParams,
    mfaClientIdForSwap?: boolean
  ) {
    if (mfaClientIdForSwap) {
      return `${swapAuthForAccessTokensParams.oauthConfig.GRANT_TYPE}=${swapAuthForAccessTokensParams.oauthConfig.AUTHORIZATION_CODE}&${swapAuthForAccessTokensParams.oauthConfig.CODE}=${swapAuthForAccessTokensParams.authorizationCode}&${swapAuthForAccessTokensParams.oauthConfig.CODE_VERIFIER}=${swapAuthForAccessTokensParams.pkcCodeGenerated.codeVerifier}&${swapAuthForAccessTokensParams.oauthConfig.CLIENT_ID}=${swapAuthForAccessTokensParams.oauthConfig.MFA_CLIENT_ID_VALUE}`;
    } else {
      return `${swapAuthForAccessTokensParams.oauthConfig.GRANT_TYPE}=${swapAuthForAccessTokensParams.oauthConfig.AUTHORIZATION_CODE}&${swapAuthForAccessTokensParams.oauthConfig.CODE}=${swapAuthForAccessTokensParams.authorizationCode}&${swapAuthForAccessTokensParams.oauthConfig.CODE_VERIFIER}=${swapAuthForAccessTokensParams.pkcCodeGenerated.codeVerifier}&${swapAuthForAccessTokensParams.oauthConfig.CLIENT_ID}=${swapAuthForAccessTokensParams.oauthConfig.CLIENT_ID_VALUE}`;
    }
  }

  exceptionHandling(exception: unknown) {
    const exceptionMetadata: IExceptionMetadata = {
      fileName,
      methodName: 'swapAuthForAccessTokens',
      exceptionCategory: EXCEPTION_CATEGORY_ENUM.JS_CATCH_BLOCK,
      exceptionMessage:
        'Exception caught while calling Swap Authorization Token for Access Token'
    };
    const exceptionStr = exception as string;
    this.exceptionTracking.track(exceptionStr, exceptionMetadata);
    return exception as SwapAuthForAccessTokens;
  }

  async returnResponseData(
    pingSwapAuthForAccessUrl: string,
    body: string,
    swapAuthForAccessTokensParams: SwapAuthForAccessTokensParams
  ) {
    const data: SwapAuthForAccessTokens = await this.http
      .post<SwapAuthForAccessTokens>(`${pingSwapAuthForAccessUrl}`, body, {
        headers: this.accessTokenHeader,
        responseType: 'json'
      })
      .toPromise()
      .catch((error: HttpErrorResponse) => {
        if (
          this.swapAuthForAccessTokensParamsErrorCodeFilter(
            swapAuthForAccessTokensParams,
            error
          ) ||
          error.error.error
        ) {
          return this.errorResponse('ERROR_MSG_128');
        }
        return error;
      });
    return data;
  }

  swapAuthForAccessTokensParamsErrorCodeFilter(
    swapAuthForAccessTokensParams: SwapAuthForAccessTokensParams,
    error: HttpErrorResponse
  ) {
    return (
      swapAuthForAccessTokensParams.oauthConfig &&
      swapAuthForAccessTokensParams.oauthConfig.EAM_OUTAGE_ERROR_CODE_IGNORE &&
      swapAuthForAccessTokensParams.oauthConfig.EAM_OUTAGE_ERROR_CODE_IGNORE.filter(
        (code) => {
          return error?.status === parseInt(code);
        }
      ).length < 1
    );
  }

  swapAuthForAccessTokensParamsValCheck(
    swapAuthForAccessTokensParams: SwapAuthForAccessTokensParams
  ) {
    return (
      !swapAuthForAccessTokensParams.authorizationCode ||
      !swapAuthForAccessTokensParams.oauthConfig ||
      !swapAuthForAccessTokensParams.pkcCodeGenerated ||
      !swapAuthForAccessTokensParams.pkcCodeGenerated.codeVerifier
    );
  }
}
